import { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import axios from 'axios';
import { 
  Building2, 
  Plus, 
  MapPin,
  Phone,
  Globe,
  Loader2,
  Edit,
  Star,
  Users,
  TrendingUp,
  MoreVertical,
  Trash2,
  CheckCircle2,
  AlertCircle,
  X,
  DollarSign,
  Hash
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Branches = () => {
  const { isDarkMode } = useOutletContext() || {};
  const { staff: currentStaff } = useAuth();
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [saving, setSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  
  const [branchForm, setBranchForm] = useState({
    branch_name: '',
    branch_code: '',
    address: '',
    city: '',
    country: 'Liberia',
    phone: '',
    currency: 'LRD',
    tax_rate: ''
  });

  const fetchBranches = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/merchant/branches?include_stats=true`, { withCredentials: true });
      setBranches(response.data);
    } catch (error) {
      console.error('Error fetching branches:', error);
      toast.error('Failed to load branches');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  const handleAddBranch = async (e) => {
    e.preventDefault();
    
    if (!branchForm.branch_name.trim()) {
      toast.error('Branch name is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...branchForm,
        tax_rate: branchForm.tax_rate ? parseFloat(branchForm.tax_rate) : null
      };
      await axios.post(`${API_URL}/api/merchant/branches`, payload, { withCredentials: true });
      toast.success('Branch created successfully');
      setShowAddModal(false);
      resetForm();
      fetchBranches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create branch');
    } finally {
      setSaving(false);
    }
  };

  const handleEditBranch = async (e) => {
    e.preventDefault();
    if (!editingBranch) return;

    setSaving(true);
    try {
      const payload = {
        ...branchForm,
        tax_rate: branchForm.tax_rate ? parseFloat(branchForm.tax_rate) : null
      };
      await axios.put(`${API_URL}/api/merchant/branches/${editingBranch.branch_id}`, payload, { withCredentials: true });
      toast.success('Branch updated successfully');
      setShowEditModal(false);
      setEditingBranch(null);
      fetchBranches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update branch');
    } finally {
      setSaving(false);
    }
  };

  const handleSetMain = async (branch) => {
    setActionLoading(branch.branch_id);
    try {
      await axios.post(`${API_URL}/api/merchant/branches/${branch.branch_id}/set-main`, {}, { withCredentials: true });
      toast.success(`${branch.branch_name} is now the main branch`);
      fetchBranches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set main branch');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (branch) => {
    if (branch.is_main) {
      toast.error('Cannot delete main branch. Set another branch as main first.');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to deactivate "${branch.branch_name}"?`)) {
      return;
    }
    
    setActionLoading(branch.branch_id);
    try {
      await axios.delete(`${API_URL}/api/merchant/branches/${branch.branch_id}`, { withCredentials: true });
      toast.success(`${branch.branch_name} has been deactivated`);
      fetchBranches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate branch');
    } finally {
      setActionLoading(null);
    }
  };

  const openEditModal = (branch) => {
    setEditingBranch(branch);
    setBranchForm({
      branch_name: branch.branch_name || '',
      branch_code: branch.branch_code || '',
      address: branch.address || '',
      city: branch.city || '',
      country: branch.country || 'Liberia',
      phone: branch.phone || '',
      currency: branch.currency || 'LRD',
      tax_rate: branch.tax_rate?.toString() || ''
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setBranchForm({
      branch_name: '',
      branch_code: '',
      address: '',
      city: '',
      country: 'Liberia',
      phone: '',
      currency: 'LRD',
      tax_rate: ''
    });
  };

  const isOwner = currentStaff?.role === 'owner';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" data-testid="branches-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className={`text-2xl font-semibold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
            Branch Management
          </h1>
          <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            {branches.length} location{branches.length !== 1 ? 's' : ''}
          </p>
        </div>
        {isOwner && (
          <Button 
            onClick={() => { resetForm(); setShowAddModal(true); }}
            className="bg-teal-500 hover:bg-teal-600 text-white"
            data-testid="add-branch-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Branch
          </Button>
        )}
      </div>

      {/* Branches Grid */}
      {branches.length === 0 ? (
        <div className={`flex-1 flex flex-col items-center justify-center rounded-xl ${isDarkMode ? 'bg-slate-800' : 'bg-white shadow-sm'}`}>
          <Building2 className={`w-16 h-16 mb-4 ${isDarkMode ? 'text-slate-600' : 'text-gray-300'}`} />
          <h3 className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
            No branches yet
          </h3>
          <p className={`text-sm mb-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            Create your first branch to get started
          </p>
          {isOwner && (
            <Button onClick={() => setShowAddModal(true)} className="bg-teal-500 hover:bg-teal-600">
              <Plus className="w-4 h-4 mr-2" />
              Create Branch
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {branches.map((branch) => (
            <div
              key={branch.branch_id}
              className={`p-5 rounded-xl border transition-all ${
                branch.is_main 
                  ? isDarkMode 
                    ? 'bg-teal-900/20 border-teal-700' 
                    : 'bg-teal-50 border-teal-200'
                  : isDarkMode 
                    ? 'bg-slate-800 border-slate-700 hover:border-slate-600' 
                    : 'bg-white border-gray-200 hover:border-gray-300 shadow-sm'
              }`}
              data-testid={`branch-${branch.branch_id}`}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className={`p-2 rounded-lg ${
                    branch.is_main 
                      ? 'bg-teal-500 text-white' 
                      : isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'
                  }`}>
                    <Building2 className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                      {branch.branch_name}
                    </h3>
                    <span className={`text-xs font-mono ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      {branch.branch_code}
                    </span>
                  </div>
                </div>
                {branch.is_main && (
                  <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                    isDarkMode ? 'bg-teal-900/50 text-teal-300' : 'bg-teal-100 text-teal-700'
                  }`}>
                    <Star className="w-3 h-3" />
                    Main
                  </span>
                )}
              </div>

              {/* Details */}
              <div className={`space-y-2 mb-4 text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                {branch.city && (
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 flex-shrink-0" />
                    <span>{branch.city}, {branch.country}</span>
                  </div>
                )}
                {branch.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 flex-shrink-0" />
                    <span>{branch.phone}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4 flex-shrink-0" />
                  <span>{branch.currency}</span>
                </div>
              </div>

              {/* Stats */}
              <div className={`flex items-center gap-4 pt-3 border-t ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}>
                <div className="flex items-center gap-1.5">
                  <Users className={`w-4 h-4 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`} />
                  <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    {branch.staff_count || 0}
                  </span>
                  <span className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>staff</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <TrendingUp className={`w-4 h-4 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`} />
                  <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    {branch.transactions_today || 0}
                  </span>
                  <span className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>today</span>
                </div>
              </div>

              {/* Actions */}
              {isOwner && (
                <div className={`flex items-center gap-2 mt-4 pt-3 border-t ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEditModal(branch)}
                    className={`flex-1 ${isDarkMode ? 'border-slate-600 hover:bg-slate-700' : ''}`}
                    data-testid={`edit-${branch.branch_id}`}
                  >
                    <Edit className="w-3 h-3 mr-1" />
                    Edit
                  </Button>
                  {!branch.is_main && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSetMain(branch)}
                        disabled={actionLoading === branch.branch_id}
                        className={`${isDarkMode ? 'border-slate-600 hover:bg-slate-700' : ''}`}
                        data-testid={`set-main-${branch.branch_id}`}
                      >
                        {actionLoading === branch.branch_id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Star className="w-3 h-3" />
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(branch)}
                        disabled={actionLoading === branch.branch_id}
                        className="text-red-500 hover:bg-red-50"
                        data-testid={`delete-${branch.branch_id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add Branch Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className={`max-w-lg ${isDarkMode ? 'bg-slate-800 border-slate-700' : ''}`}>
          <DialogHeader>
            <DialogTitle className={isDarkMode ? 'text-white' : ''}>Add New Branch</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleAddBranch} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label className={isDarkMode ? 'text-slate-300' : ''}>Branch Name *</Label>
                <Input
                  value={branchForm.branch_name}
                  onChange={(e) => setBranchForm({ ...branchForm, branch_name: e.target.value })}
                  placeholder="e.g., Main Store"
                  required
                  className={isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}
                  data-testid="branch-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label className={isDarkMode ? 'text-slate-300' : ''}>Branch Code</Label>
                <Input
                  value={branchForm.branch_code}
                  onChange={(e) => setBranchForm({ ...branchForm, branch_code: e.target.value.toUpperCase() })}
                  placeholder="Auto-generated"
                  className={isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}
                />
              </div>

              <div className="space-y-2">
                <Label className={isDarkMode ? 'text-slate-300' : ''}>Phone</Label>
                <Input
                  value={branchForm.phone}
                  onChange={(e) => setBranchForm({ ...branchForm, phone: e.target.value })}
                  placeholder="+231 88 123 4567"
                  className={isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}
                />
              </div>

              <div className="col-span-2 space-y-2">
                <Label className={isDarkMode ? 'text-slate-300' : ''}>Address</Label>
                <Input
                  value={branchForm.address}
                  onChange={(e) => setBranchForm({ ...branchForm, address: e.target.value })}
                  placeholder="Street address"
                  className={isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}
                />
              </div>

              <div className="space-y-2">
                <Label className={isDarkMode ? 'text-slate-300' : ''}>City</Label>
                <Input
                  value={branchForm.city}
                  onChange={(e) => setBranchForm({ ...branchForm, city: e.target.value })}
                  placeholder="Monrovia"
                  className={isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}
                />
              </div>

              <div className="space-y-2">
                <Label className={isDarkMode ? 'text-slate-300' : ''}>Country</Label>
                <Input
                  value={branchForm.country}
                  onChange={(e) => setBranchForm({ ...branchForm, country: e.target.value })}
                  placeholder="Liberia"
                  className={isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}
                />
              </div>

              <div className="space-y-2">
                <Label className={isDarkMode ? 'text-slate-300' : ''}>Currency</Label>
                <Select 
                  value={branchForm.currency} 
                  onValueChange={(value) => setBranchForm({ ...branchForm, currency: value })}
                >
                  <SelectTrigger className={isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="LRD">LRD (Liberian Dollar)</SelectItem>
                    <SelectItem value="USD">USD (US Dollar)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className={isDarkMode ? 'text-slate-300' : ''}>Tax Rate (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={branchForm.tax_rate}
                  onChange={(e) => setBranchForm({ ...branchForm, tax_rate: e.target.value })}
                  placeholder="0.00"
                  className={isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}
                />
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAddModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-teal-500 hover:bg-teal-600"
                disabled={saving}
                data-testid="submit-branch-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Branch'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Branch Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className={`max-w-lg ${isDarkMode ? 'bg-slate-800 border-slate-700' : ''}`}>
          <DialogHeader>
            <DialogTitle className={isDarkMode ? 'text-white' : ''}>Edit Branch</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleEditBranch} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label className={isDarkMode ? 'text-slate-300' : ''}>Branch Name *</Label>
                <Input
                  value={branchForm.branch_name}
                  onChange={(e) => setBranchForm({ ...branchForm, branch_name: e.target.value })}
                  required
                  className={isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}
                />
              </div>

              <div className="space-y-2">
                <Label className={isDarkMode ? 'text-slate-300' : ''}>Branch Code</Label>
                <Input
                  value={branchForm.branch_code}
                  onChange={(e) => setBranchForm({ ...branchForm, branch_code: e.target.value.toUpperCase() })}
                  className={isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}
                />
              </div>

              <div className="space-y-2">
                <Label className={isDarkMode ? 'text-slate-300' : ''}>Phone</Label>
                <Input
                  value={branchForm.phone}
                  onChange={(e) => setBranchForm({ ...branchForm, phone: e.target.value })}
                  className={isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}
                />
              </div>

              <div className="col-span-2 space-y-2">
                <Label className={isDarkMode ? 'text-slate-300' : ''}>Address</Label>
                <Input
                  value={branchForm.address}
                  onChange={(e) => setBranchForm({ ...branchForm, address: e.target.value })}
                  className={isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}
                />
              </div>

              <div className="space-y-2">
                <Label className={isDarkMode ? 'text-slate-300' : ''}>City</Label>
                <Input
                  value={branchForm.city}
                  onChange={(e) => setBranchForm({ ...branchForm, city: e.target.value })}
                  className={isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}
                />
              </div>

              <div className="space-y-2">
                <Label className={isDarkMode ? 'text-slate-300' : ''}>Country</Label>
                <Input
                  value={branchForm.country}
                  onChange={(e) => setBranchForm({ ...branchForm, country: e.target.value })}
                  className={isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}
                />
              </div>

              <div className="space-y-2">
                <Label className={isDarkMode ? 'text-slate-300' : ''}>Currency</Label>
                <Select 
                  value={branchForm.currency} 
                  onValueChange={(value) => setBranchForm({ ...branchForm, currency: value })}
                >
                  <SelectTrigger className={isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="LRD">LRD (Liberian Dollar)</SelectItem>
                    <SelectItem value="USD">USD (US Dollar)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className={isDarkMode ? 'text-slate-300' : ''}>Tax Rate (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={branchForm.tax_rate}
                  onChange={(e) => setBranchForm({ ...branchForm, tax_rate: e.target.value })}
                  className={isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}
                />
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowEditModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-teal-500 hover:bg-teal-600"
                disabled={saving}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Branches;
